import {
  enableProdMode,
  provideAppInitializer,
  inject,
  importProvidersFrom,
  Injector,
  provideZoneChangeDetection
} from "@angular/core";
import { createCustomElement } from "@angular/elements";
import "zone.js";
import { environment } from "@env/environment";
import {
  initializerFactory,
  DISPLAY_FORMATS,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
  MSALGuardConfigFactory,
  SchedulerStompConfigFactory,
  rxStompServiceFactory
} from "./app/factories";
import { ClientConfigService } from "@app/core/config/client-config.service";
import {
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService
} from "@azure/msal-angular";
import { RxStompConfig } from "@stomp/rx-stomp";
import { RxStompService } from "@app/services/webstomp/rx-stomp.service";
import {
  NgxHateoasClientConfigurationService,
  NgxHateoasClientModule
} from "@lagoshny/ngx-hateoas-client";
import {
  HttpClient,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi
} from "@angular/common/http";
import { AuthInterceptor } from "@app/core/auth/auth.interceptor";
import {
  DateAdapter,
  MAT_DATE_LOCALE,
  MAT_DATE_FORMATS
} from "@angular/material/core";
import {
  MomentDateAdapter,
  MatMomentDateModule
} from "@angular/material-moment-adapter";
import { AuthService } from "@app/core/auth/auth.service";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { MomentModule } from "ngx-moment";
import { TranslateModule } from "@ngx-translate/core";
import { CoreModule } from "@app/core";
import { SettingsModule } from "@app/settings";
import { SharedModule } from "@app/view/shared";
import { MarkdownModule, provideMarkdown } from "ngx-markdown";
import { UrmIntegrationModule } from "@app/integrations/urm/urm-integration.module";
import { AppRoutingModule } from "./app/app-routing.module";
import { SetupModule } from "@app/view/setup/setup.module";
import { PlanningModule } from "@app/view/planning/planning.module";
import { DiaryModule } from "@app/view/diary/diary.module";
import { JobsModule } from "@app/view/jobs/jobs.module";
import { BookingModule } from "@app/view/booking/booking.module";
import { LayoutModule } from "@angular/cdk/layout";
import { OVERLAY_DEFAULT_CONFIG } from "@angular/cdk/overlay";
import { DataViewModule } from "@app/view/data-view/data-view.module";
import {
  NgxUiLoaderModule,
  NgxUiLoaderConfig,
  POSITION,
  SPINNER
} from "ngx-ui-loader";
import { ServiceWorkerModule } from "@angular/service-worker";
import { AppComponent } from "./app/app.component";

// Import custom element components
import { WebhookRequestBodyComponent } from "@app/view/setup/system/webhook-request-body/webhook-request-body.component";
import { VisitCardComponent } from "@app/view/jobs/visit-card/visit-card.component";
import { TicketAuditRendererComponent } from "@app/view/shared/audits/ticket-audit-renderer/ticket-audit-renderer.component";
import { VisitAuditRendererComponent } from "@app/view/shared/audits/visit-audit-renderer/visit-audit-renderer.component";
import { ResourceAvailabilityAuditRendererComponent } from "@app/view/shared/audits/resource-availability-audit-renderer/resource-availability-audit-renderer.component";
import { SkillProfileDetailComponent } from "@app/view/setup/assignment/skill-profile-detail/skill-profile-detail.component";
import { MappingPatternDetailComponent } from "@app/view/setup/resourcing/mapping-patterns/mapping-pattern-detail/mapping-pattern-detail.component";
import { AuditJumpButtonComponent } from "@app/view/shared/audits/audit-jump-button/audit-jump-button.component";
import { TeamDetailComponent } from "@app/view/setup/resourcing/team/team-detail/team-detail.component";

// Define the NgxUiLoaderConfig here (doesn't need injection)
const ngxUiLoaderConfig: NgxUiLoaderConfig = {
  blur: 7,
  bgsColor: "#11395b",
  bgsPosition: POSITION.bottomRight,
  bgsSize: 100,
  bgsType: SPINNER.threeStrings
};

if (environment.production) {
  enableProdMode();
}

// Register custom elements after the application is bootstrapped
function registerCustomElements(injector: Injector) {
  // Convert Angular Components to Custom Elements and register them with browser
  customElements.define(
    "webhook-request-display",
    createCustomElement(WebhookRequestBodyComponent, { injector })
  );
  customElements.define(
    "visit-card-component",
    createCustomElement(VisitCardComponent, { injector })
  );
  customElements.define(
    "ticket-audit-renderer",
    createCustomElement(TicketAuditRendererComponent, { injector })
  );
  customElements.define(
    "visit-audit-renderer",
    createCustomElement(VisitAuditRendererComponent, { injector })
  );
  customElements.define(
    "resource-availability-audit-renderer",
    createCustomElement(ResourceAvailabilityAuditRendererComponent, {
      injector
    })
  );
  customElements.define(
    "scheduler-skill-profile-detail",
    createCustomElement(SkillProfileDetailComponent, {
      injector
    })
  );
  customElements.define(
    "app-mapping-pattern-detail-component",
    createCustomElement(MappingPatternDetailComponent, {
      injector
    })
  );
  customElements.define(
    "audit-jump-button",
    createCustomElement(AuditJumpButtonComponent, { injector })
  );
  customElements.define(
    "team-detail-component",
    createCustomElement(TeamDetailComponent, { injector })
  );

  console.log("Custom elements registered successfully");
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideMarkdown(),
    importProvidersFrom(
      // System imports
      BrowserModule,
      FontAwesomeModule,
      MomentModule,
      // Shared imports
      TranslateModule,
      CoreModule,
      SettingsModule,
      SharedModule,
      MatMomentDateModule,
      // Data service imports
      NgxHateoasClientModule.forRoot(),
      UrmIntegrationModule,
      // Application modules
      AppRoutingModule,
      SetupModule,
      PlanningModule,
      DiaryModule,
      JobsModule,
      BookingModule,
      LayoutModule,
      DataViewModule,
      // TimePicker
      NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
      ServiceWorkerModule.register("ngsw-worker.js")
    ),
    provideAppInitializer(() => {
      const initializerFn = initializerFactory(
        inject(ClientConfigService),
        inject(NgxHateoasClientConfigurationService)
      );
      return initializerFn();
    }),
    HttpClient,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE]
    },
    { provide: MAT_DATE_FORMATS, useValue: DISPLAY_FORMATS },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [ClientConfigService]
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
      deps: [ClientConfigService]
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
      deps: [ClientConfigService]
    },
    AuthService,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: RxStompConfig,
      useFactory: SchedulerStompConfigFactory,
      deps: [ClientConfigService, MsalService, MSAL_INSTANCE]
    },
    {
      provide: RxStompService,
      useFactory: rxStompServiceFactory,
      deps: [RxStompConfig, MSAL_INSTANCE]
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    // Disable CDK Popover API so overlays use traditional z-index stacking.
    // Without this, Angular CDK 21 promotes dialogs to the browser's top layer
    // (via showPopover()), which renders above ALL normal-DOM z-index values,
    // causing Bryntum's floating panels (filters, tooltips) to appear behind
    // dialog masks.
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false }
    }
  ]
})
  .then((appRef) => {
    // Register custom elements after the application is bootstrapped
    registerCustomElements(appRef.injector);

    // ServiceWorker is now fully managed by the ServiceWorkerModule in the providers array
    console.log("Application bootstrap completed successfully");
  })
  .catch((err) => console.log(err));
